import {
  CreatePostingInput,
  DEFAULT_GRAPHQL_OBJECT_DEPTH_FOR_SYNC_QUERY,
  LooseObject,
  type Posting,
  type PostingDTO,
  PostingStatus,
  UpdatePostingInput,
  cleanInputCreate,
  cleanInputUpdate,
  createPosting as createPostingMutation,
  deletePosting as deletePostingMutation,
  getPosting as getPostingQuery,
  getReadId,
  getTableClientId,
  removeNestedFieldsExcept,
  syncPostings,
  updatePosting as updatePostingMutation,
} from '@rentguru/commons-utils';
import { deleteAndHideEntityWithFetchBefore, get, list, mutation } from '@up2rent/graphql';
import { createContext, useContext, useMemo } from 'react';

import { useUser } from './UserContext';
import { usePermissions } from './utils/PermissionsContext';

export const POSTINGS_FIELDS_TO_SYNC = [
  'building',
  'unit',
  'lease',
  'charge',
  'bankAccount',
  'invoice',
  'transaction',
  'contact',
  'accountLabel',
  'leaseVariousOperation',
  'customValues',
  'tenantLandings',
  'tenantShare',
];

export const fetchPostings = async (
  by:
    | 'byCharge'
    | 'byLease'
    | 'byTransaction'
    | 'byAccountLabel'
    | 'byInvoice'
    | 'byContact'
    | 'byBuilding'
    | 'byBankAccount'
    | 'byUnit'
    | 'byForeign',
  byValue: string,
  additionalFilter?: LooseObject,
  syncPostingQuery?: string,
  options?: { includesDraftPostings?: boolean }
): Promise<Posting[]> => {
  let indexName = 'chargeId';
  // eslint-disable-next-line default-case
  switch (by) {
    case 'byLease':
      indexName = 'leaseId';
      break;
    case 'byTransaction':
      indexName = 'transactionId';
      break;
    case 'byAccountLabel':
      indexName = 'accountLabelId';
      break;
    case 'byInvoice':
      indexName = 'invoiceId';
      break;
    case 'byContact':
      indexName = 'contactId';
      break;
    case 'byBuilding':
      indexName = 'buildingId';
      break;
    case 'byBankAccount':
      indexName = 'bankAccountId';
      break;
    case 'byUnit':
      indexName = 'unitId';
      break;
    case 'byForeign':
      indexName = 'foreignId';
      break;
  }
  const filter = {
    and: [
      {
        and: [{ status: { ne: PostingStatus.REVERSAL } }, { status: { ne: PostingStatus.CANCELLED_BY_REVERSAL } }],
      },
      ...(additionalFilter ? [additionalFilter] : []),
    ],
  };

  const postings = await list<Posting>(
    syncPostingQuery ??
      removeNestedFieldsExcept(
        syncPostings,
        DEFAULT_GRAPHQL_OBJECT_DEPTH_FOR_SYNC_QUERY,
        ['vatRate'],
        POSTINGS_FIELDS_TO_SYNC
      ),
    indexName,
    byValue,
    filter
  );

  const normalizedPostings = postings.map((posting) => ({
    ...posting,
    customValues: (posting.customValues ?? []).map((c) => ({ name: c.name, value: c.value })),
  }));

  if (options?.includesDraftPostings) return normalizedPostings;
  return normalizedPostings.filter((posting) => posting.status !== PostingStatus.DRAFT);
};

export const getPosting = async (id: string) => get<Posting>(getPostingQuery, id);

export const fetchAllTransactionPostings = async (transactionId: string) => {
  const [direct, indirect] = await Promise.all([
    fetchPostings('byTransaction', transactionId),
    fetchPostings('byForeign', transactionId),
  ]);
  return [...direct, ...indirect];
};

export interface PostingContextType {
  createPosting: (input: CreatePostingInput | Omit<CreatePostingInput, 'clientId' | 'readId'>) => Promise<PostingDTO>;
  updatePosting: (updates: UpdatePostingInput) => Promise<PostingDTO>;
  deletePosting: (posting: Posting) => Promise<Posting | null>;
}

const PostingContext = createContext<PostingContextType | null>(null);

export const PostingsContextProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { clientId } = useUser();
  const { financialTransactionsDelete } = usePermissions();

  const createPosting = async (
    input: CreatePostingInput | Omit<CreatePostingInput, 'clientId' | 'readId'>
  ): Promise<PostingDTO> => {
    return mutation<PostingDTO>(createPostingMutation, {
      input: {
        ...(cleanInputCreate(input) as CreatePostingInput),
        readId: getReadId(clientId!, 'Posting'),
        clientId: getTableClientId(clientId!, 'Posting'),
      },
    });
  };

  const updatePosting = async (updates: UpdatePostingInput): Promise<PostingDTO> =>
    mutation<PostingDTO>(updatePostingMutation, { input: cleanInputUpdate(updates, false) });

  const deletePosting = async (posting: Posting): Promise<Posting | null> => {
    if (!financialTransactionsDelete) return null;
    return deleteAndHideEntityWithFetchBefore<Posting>(posting, getPostingQuery, deletePostingMutation);
  };

  const values = useMemo(
    () => ({ createPosting, updatePosting, deletePosting }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clientId, financialTransactionsDelete]
  );

  return <PostingContext.Provider value={values}>{children}</PostingContext.Provider>;
};

export const usePostings = (): PostingContextType => {
  const context = useContext<PostingContextType | null>(PostingContext);
  if (!context) {
    throw new Error('`usePostings` hook must be used within a `PostingsContextProvider` component');
  }
  return context;
};
